import { makeAutoObservable, runInAction } from 'mobx'
import { handleAxiosError } from '~/shared/lib'
import { ReportType, types } from '~/shared/api/services/reports/types'
import { ReportsServices } from '~/shared/api/services/reports'
import { FormDataReport, FormDataReportType, ReportLoadersType } from './types'
import { SelectOption } from '~/shared/ui'

type FormDataKey = keyof FormDataReportType

export class ReportsStore {
  reports: ReportType[] = []

  loaders: ReportLoadersType = {
    reports: false,
    loadMoreReports: false,
    createReport: false,
    reportsTypes: false,
  }

  formDataReport: FormDataReportType = {
    storeId: null,
    dateRange: '',
    type: null,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  reportsTypes: types[] = []

  errors: FormDataKey[] = []

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof ReportLoadersType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof ReportLoadersType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setErrorsReport = (payload: FormDataReport) => {
    const keys = Object.keys(payload) as FormDataKey[]
    this.errors = keys.filter((key) => !payload[key])
    return this.errors
  }

  clearFormDataReport = () => {
    this.formDataReport = {
      storeId: null,
      dateRange: '',
      type: null,
    }
  }

  getReports = async (action?: 'loadMoreReports') => {
    try {
      this.activeLoader(action || 'reports')

      const params = {
        limit: this.requestSettings.limit,
        offset: action === 'loadMoreReports' ? this.requestSettings.offset : 0,
      }

      const response = await ReportsServices.getReports({
        params,
      })
      const { data } = response

      if (action === 'loadMoreReports') {
        runInAction(() => {
          this.reports = [...this.reports, ...data.reports]
        })
      } else {
        runInAction(() => {
          this.reports = data.reports
        })
      }

      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: data.reports.length < this.requestSettings.limit,
        offset:
          action === 'loadMoreReports'
            ? this.requestSettings.offset + this.requestSettings.limit
            : 20,
      }

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'reports')
    }
  }

  createReport = async (data: FormDataReport) => {
    this.activeLoader('createReport')
    try {
      await ReportsServices.createReport(data)
    } finally {
      this.closeLoader('createReport')
    }
  }

  getReportsTypes = async () => {
    try {
      this.activeLoader('reportsTypes')

      const { data } = await ReportsServices.getReportsTypes()

      runInAction(() => {
        this.reportsTypes = [...this.reportsTypes, ...data]
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('reportsTypes')
    }
  }

  setFormDataReport = (
    key: keyof FormDataReportType,
    value: SelectOption | string,
  ) => {
    this.formDataReport = { ...this.formDataReport, [key]: value }
  }
}
